// src/projects/chatterbox/ChatterboxDemo.jsx
import React, { useState, useEffect, useRef, forwardRef } from "react";
import { createChatter } from "./utils/protocolSimulator";
import { useLanguage } from "../../../context/LanguageContext";
import { isWasmLoaded, resetWasmState } from "./utils/wasmLoader";
import "./chatterboxDemo.css";

// 翻译文本
const translations = {
  typeMessage: {
    en: "Type a message...",
    zh: "输入消息...",
    ja: "メッセージを入力...",
  },
  handshakeFirst: {
    en: "Perform handshake first",
    zh: "请先执行握手",
    ja: "まずハンドシェイクを実行してください",
  },
  send: {
    en: "Send",
    zh: "发送",
    ja: "送信",
  },
  protocolDetails: {
    en: "Protocol Details",
    zh: "协议细节",
    ja: "プロトコルの詳細",
  },
  showDetails: {
    en: "▶ Show details",
    zh: "▶ 显示详情",
    ja: "▶ 詳細を表示",
  },
  hideDetails: {
    en: "▼ Hide details",
    zh: "▼ 收起详情",
    ja: "▼ 詳細を隠す",
  },
};

// 辅助函数：美化显示JSON数据
const formatJsonDisplay = (data) => {
  return JSON.stringify(
    data,
    (key, value) => {
      // 对Uint8Array进行特殊处理
      if (value && value.constructor === Uint8Array) {
        return Array.from(value);
      }
      // 处理数字
      if (typeof value === "number" && String(value).includes("e")) {
        // 科学计数法数字，转为字符串后处理
        const str = value.toString();
        const parts = str.split("e");
        const base = parts[0];
        const exponent = parts[1];

        // 提取小数点前后的数字
        let digits;
        if (base.includes(".")) {
          const baseParts = base.split(".");
          digits = baseParts[0] + baseParts[1];
        } else {
          digits = base;
        }

        // 截取前16位
        const truncatedDigits = digits.substring(0, 16);
        return `${truncatedDigits.substring(0, 1)}.${truncatedDigits.substring(
          1
        )}e${exponent}`;
      }
      return value;
    },
    2
  );
};

// 使用forwardRef包装组件，允许父组件传入ref
const ChatterboxDemo = forwardRef((props, ref) => {
  const { language } = useLanguage();
  const [alice, setAlice] = useState(null);
  const [bob, setBob] = useState(null);
  const [messages, setMessages] = useState([]);
  const [handshakeComplete, setHandshakeComplete] = useState(false);
  const [inputMessage, setInputMessage] = useState("");
  const [activeUser, setActiveUser] = useState("alice");
  const [loading, setLoading] = useState(true);
  const [wasmReady, setWasmReady] = useState(false);
  const [wasmError, setWasmError] = useState(null);
  const [wasmCheckCount, setWasmCheckCount] = useState(0);
  const [showProtocolDetails, setShowProtocolDetails] = useState(false);
  const [protocolLog, setProtocolLog] = useState([]);
  const [operationError, setOperationError] = useState(null);
  const [expandedLogs, setExpandedLogs] = useState([]);
  const [autoScroll, setAutoScroll] = useState(true);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const wasmCheckInterval = useRef(null);
  const prevInputHeightRef = useRef(0);
  const scrollPositionRef = useRef(0);

  // 提供父组件可访问的方法
  React.useImperativeHandle(ref, () => ({
    // 将滚动方法暴露给父组件
    scrollToBottom: () => scrollToBottom(),
    // 获取消息容器引用
    getMessagesContainer: () => messagesContainerRef.current,
  }));

  // 清除错误信息的函数
  const clearError = () => {
    setOperationError(null);
  };

  // 错误处理和重试逻辑
  const handleOperation = async (operation, errorMessage) => {
    // 清除之前的错误
    clearError();

    try {
      // 设置加载状态
      setLoading(true);

      // 执行操作
      await operation();
    } catch (error) {
      console.error(`操作失败: ${errorMessage}`, error);

      // 设置友好的错误信息
      setOperationError({
        message: `${errorMessage}: ${error.message}`,
        time: new Date().toLocaleTimeString(),
      });

      // 如果是WebAssembly相关错误，尝试重新初始化
      if (
        error.message.includes("WebAssembly") ||
        error.message.includes("Go program") ||
        error.message.includes("wasm")
      ) {
        setWasmReady(false);
        setWasmError("WebAssembly模块出错，正在尝试重新加载...");

        // 给用户一些视觉反馈
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
    } finally {
      // 无论成功失败，都结束加载状态
      setLoading(false);
    }
  };

  // 检查WASM是否已加载
  useEffect(() => {
    console.log("开始检查WebAssembly模块加载状态...");

    // 清除之前的interval
    if (wasmCheckInterval.current) {
      clearInterval(wasmCheckInterval.current);
    }

    const maxChecks = 20; // 最多检查20次，每次500ms

    wasmCheckInterval.current = setInterval(() => {
      console.log(
        `检查WebAssembly模块 (${wasmCheckCount + 1}/${maxChecks})...`
      );

      if (isWasmLoaded()) {
        console.log("WebAssembly模块已加载，可以初始化聊天应用");
        clearInterval(wasmCheckInterval.current);
        setWasmReady(true);
        setWasmError(null);
      } else {
        // 检查是否有window.Go
        if (typeof window.Go === "undefined") {
          console.warn("警告: window.Go未定义，wasm_exec.js可能未正确加载");
        }

        setWasmCheckCount((prev) => {
          const newCount = prev + 1;
          if (newCount >= maxChecks) {
            console.error("WebAssembly模块加载超时");
            clearInterval(wasmCheckInterval.current);
            setWasmError("WebAssembly模块加载超时。请刷新页面重试。");
          }
          return newCount;
        });
      }
    }, 500);

    return () => {
      if (wasmCheckInterval.current) {
        clearInterval(wasmCheckInterval.current);
      }
    };
  }, [wasmCheckCount]);

  // Initialize chat users when WASM is ready
  useEffect(() => {
    if (!wasmReady) {
      return;
    }

    handleOperation(async () => {
      console.log("初始化聊天用户...");

      // Create Alice and Bob
      const aliceChatter = await createChatter();
      const bobChatter = await createChatter();

      setAlice(aliceChatter);
      setBob(bobChatter);
      console.log("聊天用户初始化完成");

      // Log the action
      addToProtocolLog(
        language === "en"
          ? "Initialization"
          : language === "zh"
          ? "初始化"
          : "初期化",
        language === "en"
          ? "Created identity key pairs for Alice and Bob"
          : language === "zh"
          ? "创建了Alice和Bob的身份密钥对"
          : "アリスとボブのID鍵ペアを作成しました"
      );
    }, "初始化聊天用户失败");
  }, [language, wasmReady]);

  // Auto scroll to latest message
  useEffect(() => {
    if (messagesEndRef.current && autoScroll) {
      // 不使用scrollIntoView，而是直接设置容器的scrollTop
      const messagesContainer = messagesContainerRef.current;
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }
  }, [messages, autoScroll]);

  // 监听消息容器的滚动事件
  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
      // 如果用户向上滚动，禁用自动滚动
      const isScrolledToBottom = scrollHeight - scrollTop - clientHeight < 30;
      setAutoScroll(isScrolledToBottom);

      // 保存当前滚动位置
      scrollPositionRef.current = scrollTop;
    };

    messagesContainer.addEventListener("scroll", handleScroll);
    return () => {
      messagesContainer.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // 添加到协议日志
  const addToProtocolLog = (step, details, extraData = null) => {
    setProtocolLog((prev) => [
      ...prev,
      {
        step,
        details,
        timestamp: new Date(),
        extraData, // 添加额外的详细数据
      },
    ]);
  };

  // 执行握手的封装函数
  const performHandshake = async () => {
    await handleOperation(async () => {
      if (!alice || !bob) return;

      addToProtocolLog(
        language === "en"
          ? "Handshake Step 1"
          : language === "zh"
          ? "握手步骤1"
          : "ハンドシェイクステップ1",
        language === "en"
          ? "Alice generates an ephemeral DH key pair and sends the public key to Bob"
          : language === "zh"
          ? "Alice生成临时DH密钥对并发送公钥给Bob"
          : "アリスは一時的なDH鍵ペアを生成し、公開鍵をボブに送信します",
        {
          aliceIdentity: alice.identity,
          aliceEphemeralKey: null, // 待更新
        }
      );

      // 1. Alice initiates handshake
      const aliceEphemeralPublic = await alice.initiateHandshake(bob.identity);

      // 更新上一个日志条目的额外数据
      setProtocolLog((prev) => {
        const updated = [...prev];
        if (updated.length > 0) {
          updated[updated.length - 1].extraData.aliceEphemeralKey =
            aliceEphemeralPublic;
        }
        return updated;
      });

      // 2. Bob responds to handshake
      addToProtocolLog(
        language === "en"
          ? "Handshake Step 2"
          : language === "zh"
          ? "握手步骤2"
          : "ハンドシェイクステップ2",
        language === "en"
          ? "Bob generates an ephemeral DH key pair, calculates three DH values, and sends public key to Alice"
          : language === "zh"
          ? "Bob生成临时DH密钥对，计算三组DH值，并发送公钥给Alice"
          : "ボブは一時的なDH鍵ペアを生成し、3つのDH値を計算して、公開鍵をアリスに送信します",
        {
          bobIdentity: bob.identity,
          bobEphemeralKey: null, // 待更新
          aliceEphemeralPublic,
        }
      );

      const { ephemeralPublicKey: bobEphemeralPublic, checkKey: bobCheckKey } =
        await bob.returnHandshake(alice.identity, aliceEphemeralPublic);

      // 更新上一个日志条目的额外数据
      setProtocolLog((prev) => {
        const updated = [...prev];
        if (updated.length > 0) {
          updated[updated.length - 1].extraData.bobEphemeralKey =
            bobEphemeralPublic;
          updated[updated.length - 1].extraData.bobCheckKey = bobCheckKey;
        }
        return updated;
      });

      // 3. Alice finalizes handshake
      addToProtocolLog(
        language === "en"
          ? "Handshake Step 3"
          : language === "zh"
          ? "握手步骤3"
          : "ハンドシェイクステップ3",
        language === "en"
          ? "Alice calculates three DH values and verifies that the derived check keys match"
          : language === "zh"
          ? "Alice计算三组DH值并验证派生的校验密钥是否匹配"
          : "アリスは3つのDH値を計算し、導出されたチェックキーが一致することを確認します",
        {
          bobEphemeralPublic,
          bobCheckKey,
        }
      );

      const { key: aliceCheckKey } = await alice.finalizeHandshake(
        bob.identity,
        bobEphemeralPublic
      );

      // 更新上一个日志条目的额外数据
      setProtocolLog((prev) => {
        const updated = [...prev];
        if (updated.length > 0) {
          updated[updated.length - 1].extraData.aliceCheckKey = aliceCheckKey;
        }
        return updated;
      });

      // 4. Handshake complete - check keys
      // 检查双方生成的校验密钥是否一致
      const keysMatch =
        JSON.stringify(aliceCheckKey) === JSON.stringify(bobCheckKey.key);

      addToProtocolLog(
        language === "en"
          ? "Handshake Complete"
          : language === "zh"
          ? "握手完成"
          : "ハンドシェイク完了",
        language === "en"
          ? `Handshake completed: Verification ${
              keysMatch ? "successful" : "failed"
            }`
          : language === "zh"
          ? `握手已完成: 验证${keysMatch ? "成功" : "失败"}`
          : `ハンドシェイクが完了しました: 検証${keysMatch ? "成功" : "失敗"}`,
        {
          keysMatch,
          aliceCheckKey,
          bobCheckKey,
        }
      );

      if (!keysMatch) {
        throw new Error(
          language === "en"
            ? "Check key mismatch, handshake failed"
            : language === "zh"
            ? "校验密钥不匹配，握手失败"
            : "チェックキーが一致しません、ハンドシェイクが失敗しました"
        );
      }

      setHandshakeComplete(true);
      setMessages([
        {
          text:
            language === "en"
              ? "Secure connection established"
              : language === "zh"
              ? "安全连接已建立"
              : "セキュアな接続が確立されました",
          type: "system",
          timestamp: new Date(),
        },
      ]);
    }, "握手失败");
  };

  // Send message with error handling
  const sendMessage = async () => {
    if (!inputMessage.trim() || !handshakeComplete) return;

    // 记录当前滚动位置
    const messagesContainer = messagesContainerRef.current;
    if (messagesContainer) {
      scrollPositionRef.current = messagesContainer.scrollTop;
    }

    await handleOperation(async () => {
      try {
        const sender = activeUser === "alice" ? alice : bob;
        const receiver = activeUser === "alice" ? bob : alice;

        // Send message
        addToProtocolLog(
          language === "en"
            ? "Sending Message"
            : language === "zh"
            ? "发送消息"
            : "メッセージ送信中",
          language === "en"
            ? `${
                activeUser === "alice" ? "Alice" : "Bob"
              } generates message key and encrypts the message`
            : language === "zh"
            ? `${
                activeUser === "alice" ? "Alice" : "Bob"
              }生成消息密钥并加密消息`
            : `${
                activeUser === "alice" ? "アリス" : "ボブ"
              }がメッセージキーを生成し、メッセージを暗号化します`,
          {
            plaintext: inputMessage,
            sender: sender.identity,
          }
        );

        let message;
        try {
          message = await sender.sendMessage(receiver.identity, inputMessage);

          // 更新上一个日志条目的调试信息
          setProtocolLog((prev) => {
            const updated = [...prev];
            if (updated.length > 0) {
              updated[updated.length - 1].extraData.encryptedMessage = message;
              updated[updated.length - 1].extraData._debugInfo =
                message._debugInfo;
            }
            return updated;
          });
        } catch (error) {
          console.error("发送消息失败:", error);

          // 检查是否是WASM错误
          if (
            error.toString().includes("Go program has exited") ||
            error.toString().includes("WebAssembly") ||
            error.toString().includes("WASM")
          ) {
            // 尝试重置WASM状态
            await resetSession();
            throw new Error("Go程序已重置，请重新执行握手");
          }

          throw error;
        }

        // Add sent message to UI
        setMessages((prev) => [
          ...prev,
          { text: inputMessage, sender: activeUser, timestamp: new Date() },
        ]);

        // 清除输入但保持滚动位置不变
        setInputMessage("");

        // 使用requestAnimationFrame确保DOM更新完成后再处理滚动
        if (messagesContainer) {
          requestAnimationFrame(() => {
            // 如果用户允许自动滚动，则滚动到底部
            if (autoScroll) {
              // 直接设置容器的scrollTop，而不是使用scrollIntoView
              messagesContainer.scrollTop = messagesContainer.scrollHeight;
            } else {
              // 否则恢复到之前的滚动位置
              messagesContainer.scrollTop = scrollPositionRef.current;
            }
          });
        }
      } catch (error) {
        // 重新抛出错误，让handleOperation处理
        throw error;
      }
    }, "消息发送失败");
  };

  // 重置会话
  const resetSession = async () => {
    setAlice(null);
    setBob(null);
    setMessages([]);
    setHandshakeComplete(false);
    setProtocolLog([]);
    setOperationError(null);

    // Re-initialize
    await handleOperation(async () => {
      try {
        // 确保WASM已加载正常
        if (!isWasmLoaded()) {
          await resetWasmState();
        }

        // Create Alice and Bob
        const aliceChatter = await createChatter();
        const bobChatter = await createChatter();

        setAlice(aliceChatter);
        setBob(bobChatter);

        // Log the action
        addToProtocolLog(
          language === "en"
            ? "Session Reset"
            : language === "zh"
            ? "重置会话"
            : "セッションリセット",
          language === "en"
            ? "Created new identity key pairs for Alice and Bob"
            : language === "zh"
            ? "为Alice和Bob创建了新的身份密钥对"
            : "アリスとボブの新しいIDキーペアを作成しました"
        );
      } catch (error) {
        console.error("重置会话时出错:", error);
        throw error;
      }
    }, "重置会话失败");
  };

  // 处理输入区域高度变化，阻止自动滚动
  const handleInputChange = (e) => {
    const currentHeight = e.target.scrollHeight;
    const messagesContainer = messagesContainerRef.current;

    // 记录当前滚动位置
    if (messagesContainer) {
      scrollPositionRef.current = messagesContainer.scrollTop;
    }

    setInputMessage(e.target.value);

    // 如果高度变化，保持滚动位置不变
    if (messagesContainer && currentHeight !== prevInputHeightRef.current) {
      prevInputHeightRef.current = currentHeight;
      // 使用requestAnimationFrame确保DOM更新后再设置滚动位置
      requestAnimationFrame(() => {
        messagesContainer.scrollTop = scrollPositionRef.current;
      });
    }
  };

  // 手动滚动到底部
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      // 使用scrollIntoView时添加参数阻止页面滚动
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
      setAutoScroll(true);
    }
  };

  // 显示WASM加载提示或错误信息
  if (!wasmReady) {
    return (
      <div className="chatterbox-demo-container">
        <div className="chatterbox-loading">
          <div className="spinner"></div>
          <p>
            {language === "en"
              ? `Loading WebAssembly module... (${wasmCheckCount}/20)`
              : language === "zh"
              ? `正在加载WebAssembly模块... (${wasmCheckCount}/20)`
              : `WebAssemblyモジュールを読み込んでいます... (${wasmCheckCount}/20)`}
          </p>
          {wasmError && (
            <div className="error-message">
              <p>
                {language === "en"
                  ? "Error:"
                  : language === "zh"
                  ? "错误:"
                  : "エラー:"}{" "}
                {wasmError}
              </p>
              <button
                className="retry-button"
                onClick={() => window.location.reload()}
              >
                {language === "en"
                  ? "Retry"
                  : language === "zh"
                  ? "重试"
                  : "再試行"}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="chatterbox-demo-container">
      <h2 className="chat-title">
        {language === "en"
          ? "Chatterbox Encrypted Chat Demo"
          : language === "zh"
          ? "Chatterbox 加密聊天演示"
          : "Chatterbox 暗号化チャットデモ"}
      </h2>

      <div className="chat-wrapper">
        <div className="chat-layout">
          <div className="chat-main">
            {/* 操作错误提示 */}
            {operationError && (
              <div className="operation-error">
                <button
                  className="close-button"
                  onClick={clearError}
                  aria-label="Close error"
                >
                  ×
                </button>
                <div>{operationError.message}</div>
                <div className="error-time">{operationError.time}</div>
              </div>
            )}

            <div className="chat-controls">
              <button
                className={`handshake-button ${
                  handshakeComplete ? "active" : ""
                }`}
                onClick={performHandshake}
                disabled={loading || handshakeComplete}
              >
                {loading
                  ? language === "en"
                    ? "Processing..."
                    : language === "zh"
                    ? "处理中..."
                    : "処理中..."
                  : language === "en"
                  ? "Perform Handshake"
                  : language === "zh"
                  ? "执行握手"
                  : "ハンドシェイクを実行"}
              </button>

              <button
                className="reset-button"
                onClick={resetSession}
                disabled={loading}
              >
                {language === "en"
                  ? "Reset Session"
                  : language === "zh"
                  ? "重置会话"
                  : "セッションをリセット"}
              </button>

              <button
                className={`protocol-details-button ${
                  showProtocolDetails ? "active" : ""
                }`}
                onClick={() =>
                  setShowProtocolDetails((prevState) => !prevState)
                }
              >
                {showProtocolDetails
                  ? language === "en"
                    ? "Hide Protocol Details"
                    : language === "zh"
                    ? "隐藏协议细节"
                    : "プロトコルの詳細を隠す"
                  : language === "en"
                  ? "Show Protocol Details"
                  : language === "zh"
                  ? "显示协议细节"
                  : "プロトコルの詳細を表示"}
              </button>
            </div>

            <div className="chat-content">
              <div className="user-selector">
                <button
                  className={`user-btn ${
                    activeUser === "alice" ? "active" : ""
                  }`}
                  onClick={() => setActiveUser("alice")}
                >
                  Alice
                </button>
                <button
                  className={`user-btn ${activeUser === "bob" ? "active" : ""}`}
                  onClick={() => setActiveUser("bob")}
                >
                  Bob
                </button>
              </div>

              <div className="messages-container" ref={messagesContainerRef}>
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`message ${
                      msg.type ||
                      (msg.sender === activeUser ? "sent" : "received")
                    }`}
                  >
                    {msg.type !== "system" && msg.type !== "error" && (
                      <div className="message-sender">
                        {msg.sender === "alice" ? "Alice" : "Bob"}
                      </div>
                    )}
                    <div className="message-text">{msg.text}</div>
                    <div className="message-time">
                      {msg.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />

                {/* 添加滚动到底部按钮 */}
                {messages.length > 3 && !autoScroll && (
                  <button
                    className="scroll-to-bottom-btn"
                    onClick={scrollToBottom}
                    aria-label="滚动到底部"
                  >
                    ↓
                  </button>
                )}
              </div>

              <div className="message-input">
                <textarea
                  value={inputMessage}
                  onChange={handleInputChange}
                  placeholder={
                    handshakeComplete
                      ? translations.typeMessage[language] ||
                        translations.typeMessage.en
                      : translations.handshakeFirst[language] ||
                        translations.handshakeFirst.en
                  }
                  disabled={!handshakeComplete || loading}
                  onKeyPress={(e) =>
                    e.key === "Enter" &&
                    !e.shiftKey &&
                    sendMessage() &&
                    e.preventDefault()
                  }
                  rows={1}
                  className="message-textarea"
                />
                <button
                  onClick={sendMessage}
                  disabled={
                    !handshakeComplete || loading || !inputMessage.trim()
                  }
                >
                  {translations.send[language] || translations.send.en}
                </button>
              </div>
            </div>
          </div>

          {/* 协议详情面板 - 现在总是显示在右侧，通过CSS控制显示/隐藏 */}
          <div
            className={`protocol-panel ${
              showProtocolDetails ? "visible" : "hidden"
            }`}
          >
            <h3>
              {translations.protocolDetails[language] ||
                translations.protocolDetails.en}
            </h3>
            <div className="protocol-log">
              {protocolLog.map((log, idx) => (
                <div key={idx} className="log-entry">
                  <div className="log-time">
                    {log.timestamp.toLocaleTimeString()}
                  </div>
                  <div className="log-action">{log.step}</div>
                  <div className="log-description">{log.details}</div>

                  {/* 显示额外的详细信息 */}
                  {log.extraData && (
                    <div className="log-extra-data">
                      <div
                        className="log-extra-data-toggle"
                        onClick={() => {
                          // 切换展开/折叠状态
                          const newExpandedLogs = [...expandedLogs];
                          newExpandedLogs[idx] = !newExpandedLogs[idx];
                          setExpandedLogs(newExpandedLogs);
                        }}
                      >
                        {expandedLogs[idx]
                          ? translations.hideDetails[language] ||
                            translations.hideDetails.en
                          : translations.showDetails[language] ||
                            translations.showDetails.en}
                      </div>

                      {expandedLogs[idx] && (
                        <pre className="log-extra-data-content">
                          {formatJsonDisplay(log.extraData)}
                        </pre>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ChatterboxDemo;
